import type { ReactNode } from "react";
import logoUrl from "@/assets/logo.svg";
import { Icon } from "@/components/ui/icon";
import { useTheme } from "@/lib/theme";

/**
 * Auth surface. Flat colour only: the panel sits on the warm canvas with a
 * hairline border, no gradient wash and no glass effect.
 */
export function AuthLayout({
  title,
  sub,
  wide,
  children,
}: {
  title: string;
  sub?: string;
  wide?: boolean;
  children: ReactNode;
}) {
  const theme = useTheme((s) => s.theme);
  const toggleTheme = useTheme((s) => s.toggle);

  return (
    <div className="flex min-h-[100dvh] flex-col bg-canvas">
      <header className="flex items-center justify-between px-4 py-4 md:px-6">
        <div className="flex items-center gap-2.5">
          <img
            src={logoUrl}
            alt="Mentoria"
            className="h-12 w-auto shrink-0"
            draggable={false}
          />
          <span className="text-sm font-semibold tracking-tight text-ink">Compass</span>
        </div>
        <button
          type="button"
          onClick={toggleTheme}
          aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
          className="flex h-11 w-11 items-center justify-center rounded-lg text-ink-soft transition-colors hover:bg-surface-sunken hover:text-ink active:scale-[0.98]"
        >
          <Icon name={theme === "dark" ? "sun" : "moon"} size={20} />
        </button>
      </header>

      <main className="flex flex-1 items-start justify-center px-4 py-8 md:items-center md:py-12">
        <div className={wide ? "w-full max-w-3xl" : "w-full max-w-sm"}>
          <div className="rounded-xl border border-line bg-surface p-6 md:p-8">
            <h1 className="text-xl font-semibold tracking-tight text-ink">{title}</h1>
            {sub && <p className="mt-2 text-sm leading-relaxed text-muted">{sub}</p>}
            <div className="mt-6">{children}</div>
          </div>
        </div>
      </main>
    </div>
  );
}
